import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { formatDecimal, formatMonetary, isMobileDevice, onValidate, toastError } from "../../commons/utils";
import { IContext } from "../../contexts/AuthProvider/types";
import { FileProps } from "../../s3/aws";
import { SlotProps } from "../Gallery";
import { Modal } from "../../components/Modal";
import { useAuth } from "../../contexts/AuthProvider/useAuth";
import { Grid } from "@mui/material";
import { model_fornecedor, model_imobilizado, model_motivo_requisicao, model_requisicao_forma_pagamento } from "../../models";
import { ExControls } from "../../components/ExControls";
import styled from "styled-components";
import { DataTable } from "../../components/DataTable";
import { Columns } from "../Dashboard";
import { Form } from "react-bootstrap";
import { ExButton } from "../../components/ExButton";
import { Pencil, PencilIcon } from "lucide-react";
import moment from "moment";
import { toast } from "react-toastify";

const SectionContainer = styled.div`
  border-bottom: 1px solid var(--line-border-color);
  padding: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
`;

const InfoBlock = styled.div`
  border-bottom: 1px solid var(--line-border-color);
  padding: 10px 0;
`;

const UrgenteTag = styled.span`
  color: #f15619;
  font-weight: 600;
  font-size: 13px;
`;

const Itens = styled.div`
  border-top: 1px solid var(--line-border-color);
  padding: 6px 0;
`;

const FontFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    font-size: 13px;
`

export const model_observacao = {
    inputs: [
        { id: 'motivo', label: 'Informe o motivo do retorno da situação (opcional)', type: 'text', md: 12, textarea: true, rows: 3 },
    ]
}

export async function getFilesRequisicao(auth: any, reqid: string, usucodigo: string): Promise<SlotProps[]> {
    try {
        const response: any = await auth.get('/v1/requisicaoimagensone?reqid=' + reqid + '&usucodigo=' + usucodigo);

        if (response?.data) {
            const slots: SlotProps[] = response.data.filter((e: any) => e._delete !== 1).map((item: any) => ({
                ...item,
                uuid: item.reqimgid,
                url: item.reqimgurl,
                type: item.reqimgurl.toLowerCase().endsWith(".pdf") ? 'document' : 'image',
                name: ''
            }));

            return slots;
        } else
            return [];
    } catch (error: any) {
        toastError(`Erro ao carregar arquivos da requisição: ${error.message || error}`);
        return [];
    }
}

export async function deleteFileRequisicao(auth: IContext, reqid: any, file: any) {
    const body = {
        ...file,
        _delete: 1
    };

    try {
        await auth.deleteRequest('/v1/requisicaoimagens', body);
    } catch (error: any) {
        toastError(error);
    }
}

export async function uploadFileRequisicao(auth: IContext, reqid: string, file: FileProps, usucodigo?: number) {
    const body = {
        reqimgid: file.uuid,
        reqid,
        reqimgurl: file.url,
        usucodigo
    };

    try {
        const retorno: any = await auth.post('/v1/requisicaoimagens', body);
        return { ...retorno.data, uuid: retorno.data.reqimgid }
    } catch (error: any) {
        toastError(error);
    }
}

export function hasPrivilegio(status: any[], privilegios: any[], campos: any[], current_id: number, next: boolean, valorTotal: number): { result: boolean, isValueLimit?: boolean, statid?: number } {
    const current_status = status.find((item) => item.id === current_id);

    if (!current_status) return ({ result: false });

    const possibles = status.filter((item) =>
        next ? item.sequencia > current_status.sequencia : item.sequencia < current_status.sequencia
    );

    const new_status = possibles.reduce((adjacente, atual) =>
        (!adjacente || (next ? atual.sequencia < adjacente.sequencia : atual.sequencia > adjacente.sequencia)) ? atual : adjacente, null
    );

    if (new_status) {
        if (next && (new_status.assinatura === 1)) return ({ result: false });

        const privilegio = privilegios?.find((p) => p.statid === new_status.id);

        if (privilegio?.usugrustvalormaximo && (valorTotal || 0) > privilegio?.usugrustvalormaximo)
            return ({ result: false, isValueLimit: true })
        else if (privilegio?.usugrustincluir === 1)
            return ({ result: true, statid: new_status.id })
        else
            return ({ result: false })
    }

    return ({ result: new_status?.id !== undefined, statid: new_status?.id });
}

export async function getUsuPrivilegio(auth: IContext) {
    try {
        const response: any = await auth.get(`/v1/usuariogrupostatuspermissaodesc?gruid=${auth.grupo.gruid}&usucodigo=${auth.usucodigo}`);
        return response.data;
    } catch (error: any) {
        toastError(error);
    }
}

export async function getCampos(auth: IContext) {
    try {
        const response: any = await auth.get(`/v1/requisicaocampos?gruid=${auth.grupo.gruid}&reqcampoativo=1`);
        return response.data;
    } catch (error: any) {
        toastError(error);
    }
}

export async function getStatus(auth: IContext) {
    try {
        const response: any = await auth.get('/v1/status');
        const filteredData = response.data
            .filter((item: any) => item.statativo === 1)
            .sort((a: any, b: any) => (a.statsequencia + a.statrejeicao * 100000) - (b.statsequencia + b.statrejeicao * 100000));

        const _status = filteredData
            .map((item: any) => ({
                id: item.statid,
                title: item.statdescricao,
                color: item.statcor ? (item.statcor.length >= 9 ? item.statcor.slice(0, -2) : item.statcor) : (item.statrejeicao === 1 ? '#F62e2E' : ''),
                value: item.statid,
                sequencia: item.statsequencia,
                assinatura: item.statassinatura,
                rejeicao: item.statrejeicao
            }));

        return _status;
    } catch (error: any) {
        toastError(error);
    }
}

export async function updateStatus(
    auth: any,
    status: any[],
    privilegios: any[],
    campos: any[],
    current_data: any,
    next: boolean,
    valorTotal: number,
    openModalCampos?: (requisicao: any, camposPendentes: any[]) => void
) {
    const { statid: reqsituacao, result } = hasPrivilegio(status, privilegios, [], current_data.statid, next, valorTotal);

    if (!result) return;

    if (next) {
        const current_status = status.find((item) => item.id === current_data.statid);

        let possibles = status.filter((item) => item.sequencia > current_status.sequencia);

        if (current_data.rejeicao === 1)
            possibles = possibles.filter((item) => item.rejeicao === 1)
        else
            possibles = possibles.filter((item) => item.rejeicao === 0);

        const new_status = possibles.reduce(
            (adjacente, atual) =>
                !adjacente || atual.sequencia < adjacente.sequencia ? atual : adjacente,
            null
        );

        if (!new_status) return;

        const [camposResponse, valoresResponse]: any = await Promise.all([
            auth.get(`/v1/requisicaocamposfull?reqsituacao=${new_status.id}`),
            auth.get(`/v1/requisicaocamposvaloresone?reqid=${current_data.reqid}&usucodigo=${current_data.usucodigo}`)
        ]);

        const campos = camposResponse.data;
        const valores = valoresResponse.data;

        const camposComValores = campos.map((campo: any) => {
            const valorEncontrado = valores.find((v: any) => v.reqcamponome === campo.reqcamponome);
            return {
                ...campo,
                valor: valorEncontrado?.reqcampovalor ?? "",
            };
        });

        const camposPendentes = camposComValores.filter(
            (campo: any) =>
                CUSTOM_FIELDS_CAPTIONS[campo.reqcamponome] &&
                (!campo.valor || campo.valor.trim() === "")
        );

        if (openModalCampos && camposPendentes.length > 0) {
            openModalCampos({ ...current_data, next_status_id: new_status.id }, camposPendentes);
            return;
        }
    }

    await auth.post(
        "/v1/requisicaostatuscrm",
        {
            reqid: current_data.reqid,
            usucodigo: current_data.usucodigo,
            reqsituacao,
            reqhistmotivo: current_data.motivo,
        },
        {
            reqid: current_data.reqid,
            usucodigo: current_data.usucodigo
        }
    ).catch((error: any) => {
        console.log(error);
    });
}

export const CUSTOM_FIELDS_CAPTIONS: Record<string, any> = {
    reqnumeronotafiscal: { caption: 'Nº NF', type: 'number' },
    reqnumeropedidofornecedor: { caption: 'Pedido', type: 'number' },
    reqnumerocotacaoagro: { caption: 'Cotação Nº', type: 'number' },
    reqdtvencimento: { caption: 'Data de Vencimento', type: 'date' },
    reqdtentrega: { caption: 'Data de Entrega', type: 'date' },
    reqforpagid: { caption: 'Forma de Pagamento', type: 'select', model: model_requisicao_forma_pagamento },
    reqforpagcomplemento: { caption: 'Complemento', type: 'text', textarea: true, rows: 2 },
    reqqtdparcelas: { caption: 'Quantidade de Parcelas', type: 'number' },
    reqocorrencia: { caption: 'Ocorrência', type: 'text', textarea: true, rows: 2 },
    reqdtrequerida: { caption: 'Data Requerida', type: 'date' },
    motivo: { caption: 'Motivo', type: 'text', textarea: true, rows: 2 }
};

interface ModalRequisicaoProps {
    success?: () => any;
}

export const ModalRequisicao = forwardRef((props: ModalRequisicaoProps, ref) => {
    const auth = useAuth();
    const refmodal = useRef<any>(null);

    const refEditarItem = useRef<any>(null);
    const [requisicao, setRequisicao] = useState<any>();
    const [campos, setCampos] = useState<any[]>([]);
    const [itens, setItens] = useState<any[]>([]);
    const [validated, setValidated] = useState(false);
    const [permiteEditarPrecos, setPermiteEditarPrecos] = useState(false);
    const [edit, setEdit] = useState(false);
    const [editMode, setEditMode] = useState(false);

    const inputsEls = useRef<{ [key: string]: any }>({});

    async function openModal(_requisicao: any) {
        async function loadPrivilegio() {
            auth.get('v1/usuariogrupostatuspermissao?gruid=' + auth.grupo.gruid + '&usucodigo=' + auth.usucodigo + '&statid=' + _requisicao.statid)
                .then((response: any) => {
                    if (response.data.length > 0)
                        setEdit(response.data[0].usugrualterar === 1)
                })
                .catch((error) => toastError(error))
        }

        async function getCampos() {
            try {
                if (!_requisicao) return;

                const [camposResponse, valoresResponse, itens]: any = await Promise.all([
                    auth.get(`/v1/requisicaocamposfull?reqsituacao=${_requisicao.statid}`),
                    auth.get(`/v1/requisicaocamposvaloresone?reqid=${_requisicao.reqid}&usucodigo=${_requisicao.usucodigo}`),
                    auth.get(`/v1/requisicaoitensone?reqid=${_requisicao.reqid}&usucodigo=${_requisicao.usucodigo}&reqitemsituacao=1`)
                ]);

                const campos = camposResponse.data;
                const valores = valoresResponse.data;

                const camposComValores = campos.map((campo: any) => {
                    const valorEncontrado = valores.find((v: any) => v.reqcamponome === campo.reqcamponome);
                    return {
                        ...campo,
                        valor: valorEncontrado?.reqcampovalor ?? '',
                    };
                });

                setCampos(camposComValores);

                const campoPrecos = campos.find((c: any) => c.reqcamponome === 'precos');

                console.log(campoPrecos)

                let permiteEditarPrecos = false;

                if (!campoPrecos || campoPrecos.statid == null || campoPrecos.statid >= _requisicao.statid)
                    permiteEditarPrecos = true;

                setPermiteEditarPrecos(permiteEditarPrecos);

                setItens(itens.data);
            } catch (error) {
                toastError(error);
            }
        }

        setRequisicao(null);
        setCampos([]);
        setItens([]);
        setValidated(false);
        setEditMode(false);
        getCampos();
        setRequisicao(_requisicao);

        await loadPrivilegio()

        refmodal.current.openModal();
    }

    useImperativeHandle(ref, () => ({
        openModal
    }));

    async function handleSaveClick() {
        async function update() {
            const fields_requisicao = ['motid', 'imobid', 'reqobservacao'];

            const _requisicao = {
                ...requisicao,
                ...fields_requisicao.reduce((obj: any, field: any) => {
                    obj[field] = inputsEls.current[field].getValue();
                    return obj;
                }, {})
            };

            _requisicao.reqfornecedor = inputsEls.current['forid'].getValue();

            delete _requisicao.fornome;
            delete _requisicao.imobdescricao;
            delete _requisicao.impdescricao;
            delete _requisicao.motivo;
            delete _requisicao.statdescricao;
            delete _requisicao.usunome;

            fields_requisicao.forEach(field => {
                delete inputsEls.current[field];
            });

            delete inputsEls.current['forid'];

            const campos_valores = Object.keys(inputsEls.current)
                .map(field => {
                    const type = CUSTOM_FIELDS_CAPTIONS[field]?.type || 'text';

                    const value = inputsEls.current[field].getValue() ?? '';
                    let original = campos.find(c => c.reqcamponome === field) ?? '';

                    if (type === 'date' && original.valor)
                        original.valor = moment(original.valor).format('YYYY-MM-DD')

                    if (value !== original.valor)
                        return {
                            reqid: requisicao.reqid,
                            usucodigo: requisicao.usucodigo,
                            reqcamponome: field,
                            reqcampovalor: value
                        };

                    return null;
                })
                .filter(Boolean);

            await auth.post('v1/requisicao', _requisicao)
                .catch((error) => {
                    toastError(error)
                    return;
                })

            if (campos_valores.length > 0) {
                await auth.post('v1/requisicaocamposvaloreslist', campos_valores)
                    .catch((error) => {
                        toastError(error)
                        return;
                    })
            }

            toast.success('Requisição atualizada.')

            if (props.success)
                props.success();

            refmodal.current.closeModal();
        }

        setValidated(true);

        if (onValidate(inputsEls))
            update();
    }

    const columns = [
        { field: 'reqitemdescricao', header: 'Descrição' },
        { field: 'reqitemquantidade', header: 'Quantidade', body: (e: any) => formatDecimal(e.reqitemquantidade), dataType: 'numeric' },
        { field: 'reqitemunitario', header: 'Unitário', body: (e: any) => formatMonetary(e.reqitemunitario), dataType: 'numeric' },
        { field: 'total', header: 'Total', body: (e: any) => formatMonetary(e.reqitemquantidade * e.reqitemunitario), dataType: 'numeric' },
        edit && {
            field: 'edit',
            notSortable: true,
            body: (e: any) => {
                const disabled = !permiteEditarPrecos;

                return (
                    <ExButton
                        size='sm'
                        className='m-0 me-2 p-1'
                        color={'var(--primary-color)'}
                        style={{ borderRadius: '6px' }}
                        onClick={() => refEditarItem.current.openModal(e)}
                        iconcenter={<Pencil size={20} />}
                        title={disabled ? 'Edição de preços não permitida para esta situação' : 'Editar item'}
                    />
                )
            },
            style: { minWidth: '2rem', width: '2rem' }
        }
    ].filter(e => e);

    const groupFooter = () => {
        const total = itens.reduce((acc: number, item: any) => acc + (item.reqitemquantidade * item.reqitemunitario || 0), 0);

        return (
            <React.Fragment>
                <td colSpan={12} style={{ padding: '10px' }}>
                    <FontFooter>Total Itens: {formatMonetary(total)}</FontFooter>
                    <FontFooter>- Desconto: {formatMonetary(requisicao?.reqdesconto)}</FontFooter>
                    <FontFooter>= Total: {formatMonetary(total - requisicao?.reqdesconto)}</FontFooter>
                </td>
            </React.Fragment >
        );
    };

    const headerOptions = () => {
        return (
            edit &&
            <ExButton icon={<PencilIcon size={20} />} disabled={editMode} onClick={() => setEditMode(true)}>Editar</ExButton>
        )
    }

    return (
        <Modal
            ref={refmodal}
            title={'Requisição - ' + requisicao?.reqnumero}
            cancel={() => refmodal.current.closeModal()}
            success={editMode && handleSaveClick}
            header={headerOptions}
            size="lg"
        >
            <Form noValidate validated={validated}>
                <Grid container spacing={2} style={{ fontSize: '13px' }}>
                    <Grid item xs={12}>
                        <SectionContainer>
                            <div>{requisicao?.impdescricao}</div>
                            {requisicao?.impprioridade === 1 && <UrgenteTag>Urgente</UrgenteTag>}
                            <div>Solicitante: {requisicao?.usunome}</div>
                        </SectionContainer>

                        <InfoBlock>
                            <Grid container spacing={2} style={{ padding: '10px' }}>
                                {[
                                    { id: "motivo", field: 'motid', label: "Motivo", model: model_motivo_requisicao },
                                    { id: "fornecedor", field: 'forid', label: "Fornecedor", model: model_fornecedor },
                                    { id: "imobilizado", field: 'imobid', label: "Imobilizado", model: model_imobilizado }
                                ].map(({ id, field, label, model }) => (
                                    <Grid item xs={isMobileDevice() ? 12 : 6} key={id}>
                                        <ExControls
                                            id={id}
                                            ref={(e: any) => inputsEls.current[field] = e}
                                            type="select"
                                            label={label}
                                            model={model}
                                            defaultValue={requisicao?.[field]}
                                            validated={validated}
                                            disabled={!editMode}
                                            copy
                                            required={campos.find(c => c.reqcamponome === id)?.reqcampoobrigatorio === 1}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </InfoBlock>
                    </Grid>


                    {
                        campos.map((campo, index) => {
                            const fieldTranslate = CUSTOM_FIELDS_CAPTIONS[campo.reqcamponome];
                            if (!fieldTranslate) return null;

                            const refId = campo.reqcamponome + index;

                            return (
                                <React.Fragment key={refId}>
                                    <Grid item xs={4}>
                                        {(campo.reqcampoobrigatorio === 1 ? '*' : '') + fieldTranslate.caption}
                                    </Grid>
                                    <Grid item xs={8}>
                                        <ExControls
                                            id={refId}
                                            ref={(e: any) => inputsEls.current[campo.reqcamponome] = e}
                                            type={fieldTranslate.type}
                                            model={fieldTranslate.model}
                                            textarea={fieldTranslate.textarea}
                                            rows={fieldTranslate.rows}
                                            defaultValue={campo.valor}
                                            copy
                                            required={campo.reqcampoobrigatorio === 1}
                                            disabled={!editMode}
                                        />
                                    </Grid>
                                </React.Fragment>
                            );
                        })
                    }

                    {itens.length > 0 && (
                        <Grid item xs={12} >
                            <Itens>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', marginBottom: '4px' }}>Itens</div>
                                <DataTable
                                    value={itens}
                                    originvalues={itens}
                                    disabledrefresh
                                    disableheader
                                    rowGroupMode={'subheader'}
                                    groupRowsBy={'reqid'}
                                    rowGroupFooterTemplate={groupFooter}
                                >
                                    {Columns(columns)}
                                </DataTable>
                            </Itens>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <ExControls
                            id="observacao"
                            ref={(e: any) => inputsEls.current['reqobservacao'] = e}
                            label="Observação"
                            type="text"
                            defaultValue={requisicao?.reqobservacao}
                            textarea
                            rows={2}
                            copy
                            disabled={!editMode}
                        />
                    </Grid>
                </Grid>
            </Form>
            <ModalEditarItemRequisicao
                ref={refEditarItem}
                permiteEditarPrecos={permiteEditarPrecos}
                success={(updatedItem) => {
                    setItens((prev) => prev.map((item) => item.reqitemid === updatedItem.reqitemid ? updatedItem : item));
                }}
            />
        </Modal >
    );
});

export const ModalRequisicaoCamposPendentes = forwardRef(
    (props: any, ref) => {
        const auth = useAuth();

        const refmodal = useRef<any>(null);
        const [requisicao, setRequisicao] = useState<any>();
        const [campos, setCampos] = useState<any[]>([]);
        const inputsEls = useRef<{ [key: string]: any }>({});

        async function openModal(_requisicao: any, camposPendentes: any[]) {
            setRequisicao(_requisicao);
            setCampos(camposPendentes);
            refmodal.current.openModal();
        }

        useImperativeHandle(ref, () => ({
            openModal
        }));

        async function handleSaveClick() {
            try {
                const camposInvalidos: string[] = [];

                for (const campo of campos) {
                    const el = inputsEls.current[campo.reqcamponome];
                    const value = el?.getValue();

                    const config = CUSTOM_FIELDS_CAPTIONS[campo.reqcamponome];
                    const tipo = config?.type;
                    const descricao = config?.caption || campo.reqcampodescricao || campo.reqcamponome;

                    if (campo.reqcampoobrigatorio === 1) {

                        if ((value === null || value === undefined) || (typeof value === "string" && value.trim() === "")) {
                            camposInvalidos.push(`[${descricao}]`);
                            continue;
                        }
                    }

                    if (tipo === "monetary") {
                        const numberValue = Number(value);

                        if (isNaN(numberValue) || numberValue <= 0) {
                            camposInvalidos.push(`[${descricao}] (deve ser maior que zero)`);
                        }
                    }
                }

                if (camposInvalidos.length > 0) {
                    toast.error(
                        <>
                            <div>Por favor, informe todos os campos obrigatórios:</div>
                            <br />
                            {camposInvalidos.map((c, i) => (
                                <div key={i}>{c}</div>
                            ))}
                        </>
                    );
                    return;
                }

                const campos_valores = campos.map((campo) => ({
                    reqid: requisicao.reqid,
                    usucodigo: requisicao.usucodigo,
                    reqcamponome: campo.reqcamponome,
                    reqcampovalor: inputsEls.current[campo.reqcamponome]?.getValue() ?? ""
                }));

                await auth.post("v1/requisicaocamposvaloreslist", campos_valores);

                toast.success("Campos preenchidos com sucesso!");

                if (requisicao.next_status_id) {
                    await auth.post("/v1/requisicaostatuscrm",
                        {
                            reqid: requisicao.reqid,
                            usucodigo: requisicao.usucodigo,
                            reqsituacao: requisicao.next_status_id,
                            reqhistmotivo: requisicao.motivo
                        },
                        {
                            reqid: requisicao.reqid,
                            usucodigo: requisicao.usucodigo
                        }
                    );
                }

                if (props.success) props.success();

                refmodal.current.closeModal();
            } catch (error) {
                toastError(error);
            }
        }

        return (
            <Modal
                ref={refmodal}
                title={`Preencher campos pendentes - Req ${requisicao?.reqnumero}`}
                cancel={() => refmodal.current.closeModal()}
                success={handleSaveClick}
                size="lg"
            >
                <Form>
                    <Grid container spacing={2}>
                        {campos.map((campo, index) => {
                            const fieldTranslate = CUSTOM_FIELDS_CAPTIONS[campo.reqcamponome];
                            if (!fieldTranslate) return null;

                            const refId = campo.reqcamponome + index;

                            return (
                                <React.Fragment key={refId}>
                                    <Grid item xs={4}>
                                        {(campo.reqcampoobrigatorio === 1 ? "*" : "") +
                                            fieldTranslate.caption}
                                    </Grid>
                                    <Grid item xs={8}>
                                        <ExControls
                                            id={refId}
                                            ref={(e: any) =>
                                                (inputsEls.current[campo.reqcamponome] = e)
                                            }
                                            type={fieldTranslate.type}
                                            model={fieldTranslate.model}
                                            textarea={fieldTranslate.textarea}
                                            rows={fieldTranslate.rows}
                                            defaultValue={campo.valor}
                                            required={campo.reqcampoobrigatorio === 1}
                                        />
                                    </Grid>
                                </React.Fragment>
                            );
                        })}
                    </Grid>
                </Form>
            </Modal>
        );
    }
);

interface ModalEditarItemProps {
    permiteEditarPrecos: boolean;
    success?: (updatedItem: any) => void;
}

export const ModalEditarItemRequisicao = forwardRef((props: ModalEditarItemProps, ref) => {
    const auth = useAuth();
    const refModal = useRef<any>(null);
    const inputsEls = useRef<{ [key: string]: any }>({});
    const [validated, setValidated] = useState(false);
    const [item, setItem] = useState<any>();

    useImperativeHandle(ref, () => ({
        openModal
    }));

    function openModal(_item: any) {
        setItem(_item);
        setValidated(false);
        setTimeout(() => {
            recalcTotal();
            refModal.current.openModal();
        }, 10);
    }

    function recalcTotal() {
        const quantidade = parseFloat(inputsEls.current["quantidade"]?.getValue() || 0);
        const unitario = parseFloat(inputsEls.current["unitario"]?.getValue() || 0);
        const totalValue = quantidade * unitario;

        if (inputsEls.current["total"]) {
            inputsEls.current["total"].setValue(formatMonetary(totalValue));
        }
    }

    async function handleSaveClick() {
        setValidated(true);

        if (!onValidate(inputsEls)) return;

        try {
            const updatedItem = {
                ...item,
                reqitemdescricao: inputsEls.current["descricao"].getValue(),
                reqitemquantidade: parseFloat(inputsEls.current["quantidade"].getValue() || 0),
                reqitemunitario: parseFloat(inputsEls.current["unitario"].getValue() || 0),
            };

            await auth.post('/v1/requisicaoitens', updatedItem);

            toast.success("Item atualizado com sucesso!");
            refModal.current.closeModal();

            if (props.success) props.success(updatedItem);
        } catch (error) {
            toastError(error);
        }
    }

    return (
        <Modal
            ref={refModal}
            title={"Editar item"}
            cancel={() => refModal.current.closeModal()}
            success={handleSaveClick}
        >
            <Form noValidate validated={validated}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <ExControls
                            id="descricao"
                            label="Descrição"
                            type="text"
                            defaultValue={item?.reqitemdescricao}
                            ref={(el: any) => (inputsEls.current["descricao"] = el)}
                            required
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <ExControls
                            id="quantidade"
                            label="Quantidade"
                            type="number"
                            defaultValue={item?.reqitemquantidade}
                            ref={(el: any) => (inputsEls.current["quantidade"] = el)}
                            onAfterChange={() => recalcTotal()}
                            required
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <ExControls
                            id="unitario"
                            label="Valor Unitário"
                            type="number"
                            defaultValue={item?.reqitemunitario}
                            ref={(el: any) => (inputsEls.current["unitario"] = el)}
                            onAfterChange={() => recalcTotal()}
                            required
                            disabled={!props.permiteEditarPrecos}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <ExControls
                            id="total"
                            label="Total"
                            type="text"
                            defaultValue={formatMonetary((item?.reqitemquantidade || 0) * (item?.reqitemunitario || 0))}
                            ref={(el: any) => (inputsEls.current["total"] = el)}
                            disabled
                        />
                    </Grid>
                </Grid>
            </Form>
        </Modal>
    );
});